/**
 * Evaluate country risk level based on REAL extracted metrics.
 * 
 * Logic:
 * - UNASSESSED: No Country Snapshot generated (no data available)
 * - STABLE: Investment-grade credit (BBB- or above), OR HDI >= 0.75
 * - CAUTION: Sub-investment-grade credit (B to BB+), OR HDI 0.55-0.74
 * - HIGH RISK: Very low credit (below B), OR HDI < 0.55
 * - MODERATE: Has some data but doesn't clearly fit above categories
 * 
 * Priority: Credit Rating > HDI > Sovereign Score > fallback to section count
 */

const INVESTMENT_GRADE = ['AAA', 'AA+', 'AA', 'AA-', 'A+', 'A', 'A-', 'BBB+', 'BBB', 'BBB-'];
const SUB_INVESTMENT = ['BB+', 'BB', 'BB-', 'B+', 'B', 'B-'];
const HIGH_RISK_RATINGS = ['CCC+', 'CCC', 'CCC-', 'CC', 'C', 'D', 'RD', 'SD'];

function extractFirstRating(ratingStr) {
  if (!ratingStr) return null;
  // Match patterns like "B+", "BBB-", "Baa1", "Ba2" etc.
  const match = ratingStr.match(/\b(AAA|AA\+|AA-|AA|A\+|A-|A|BBB\+|BBB-|BBB|BB\+|BB-|BB|B\+|B-|B|CCC\+|CCC-|CCC|CC|C|D|RD|SD)\b/i);
  return match ? match[1].toUpperCase() : null;
}

export function evaluateRisk(country) {
  const hasSnapshot = (country.sections_generated || []).includes('country_snapshot');
  const creditRating = country.metrics_credit_rating;
  const hdi = country.metrics_hdi;
  const sovereignScore = country.metrics_sovereign_score;
  const gdpGrowth = country.metrics_gdp_growth;

  // No data at all — UNASSESSED
  if (!hasSnapshot && !creditRating && hdi == null && sovereignScore == null) {
    return { level: 'UNASSESSED', color: 'gray', colorHex: '#9ca3af' };
  }

  // Try credit rating first (most reliable indicator)
  const rating = extractFirstRating(creditRating);
  if (rating) {
    if (INVESTMENT_GRADE.includes(rating)) {
      return { level: 'STABLE', color: 'green', colorHex: '#3b82f6' };
    }
    if (HIGH_RISK_RATINGS.includes(rating)) {
      return { level: 'HIGH RISK', color: 'red', colorHex: '#ef4444' };
    }
    // Sub-investment grade (B to BB+)
    if (['BB+', 'BB', 'BB-'].includes(rating)) {
      return { level: 'CAUTION', color: 'yellow', colorHex: '#f59e0b' };
    }
    if (['B+', 'B', 'B-'].includes(rating)) {
      return { level: 'MODERATE', color: 'yellow', colorHex: '#f59e0b' };
    }
  }

  // Try HDI
  if (hdi != null) {
    if (hdi >= 0.75) return { level: 'STABLE', color: 'green', colorHex: '#3b82f6' };
    if (hdi >= 0.55) return { level: 'CAUTION', color: 'yellow', colorHex: '#f59e0b' };
    return { level: 'HIGH RISK', color: 'red', colorHex: '#ef4444' };
  }

  // Try sovereign score (assumes 0-10 scale)
  if (sovereignScore != null) {
    if (sovereignScore >= 7) return { level: 'STABLE', color: 'green', colorHex: '#3b82f6' };
    if (sovereignScore >= 4) return { level: 'CAUTION', color: 'yellow', colorHex: '#f59e0b' };
    return { level: 'HIGH RISK', color: 'red', colorHex: '#ef4444' };
  }

  // Has snapshot but couldn't extract clear metrics — MODERATE
  if (hasSnapshot) {
    return { level: 'MODERATE', color: 'yellow', colorHex: '#f59e0b' };
  }

  return { level: 'UNASSESSED', color: 'gray', colorHex: '#9ca3af' };
}
